/**
 * @file src/utils/formatters.js
 * @description Shared pure formatting functions used across the application.
 *
 * Rules:
 *  - Pure functions only — no React, no axios, no side effects
 *  - Every function takes a raw value and returns a formatted string
 *  - Locale is Saudi Arabia (ar-SA / en-SA) unless explicitly overridden
 *  - All monetary values are in SAR unless stated otherwise
 *  - Fully unit-testable with no setup required
 */

// ---------------------------------------------------------------------------
// Currency
// ---------------------------------------------------------------------------

/**
 * Format a number as Saudi Riyal (SAR).
 * Output: "SAR 1,234.56"
 *
 * @param {number|string} value
 * @param {number} [decimalPlaces=2]
 * @returns {string}
 */
export const formatCurrency = (value, decimalPlaces = 2) => {
  const num = Number(value);
  if (isNaN(num)) return "SAR 0.00";
  return new Intl.NumberFormat("en-SA", {
    style: "currency",
    currency: "SAR",
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(num);
};

/**
 * Format a number as a plain decimal string with fixed decimal places.
 * Used for invoice line amounts in XML payloads (not for display).
 * Output: "1234.56"
 *
 * @param {number|string} value
 * @param {number} [decimalPlaces=2]
 * @returns {string}
 */
export const formatDecimal = (value, decimalPlaces = 2) => {
  const num = Number(value);
  if (isNaN(num)) return "0.00";
  return num.toFixed(decimalPlaces);
};

/**
 * Format a VAT percentage for display.
 * Output: "15%"
 *
 * @param {number|string} value
 * @returns {string}
 */
export const formatVatRate = (value) => {
  const num = Number(value);
  if (isNaN(num)) return "0%";
  return `${num}%`;
};

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/**
 * Format a date for display in the UI.
 * Output: "15 Apr 2026"
 *
 * @param {Date|string|number} value
 * @returns {string}
 */
export const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-SA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

/**
 * Format a date and time for display in the UI.
 * Output: "15 Apr 2026, 14:30"
 *
 * @param {Date|string|number} value
 * @returns {string}
 */
export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-SA", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

/**
 * Format a date to ZATCA XML required format.
 * Rule (BR-KSA-F-01): YYYY-MM-DD
 * Output: "2026-04-15"
 *
 * @param {Date|string|number} value
 * @returns {string}
 */
export const formatDateForXml = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

/**
 * Format a time to ZATCA XML required format.
 * Rule (BR-KSA-70): HH:mm:ss (local AST) or HH:mm:ssZ (UTC)
 * Output: "14:30:00"
 *
 * @param {Date|string|number} value
 * @param {boolean} [utc=false] - If true, append Z suffix for UTC
 * @returns {string}
 */
export const formatTimeForXml = (value, utc = false) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  const hours = String(utc ? date.getUTCHours() : date.getHours()).padStart(
    2,
    "0",
  );
  const minutes = String(
    utc ? date.getUTCMinutes() : date.getMinutes(),
  ).padStart(2, "0");
  const seconds = String(
    utc ? date.getUTCSeconds() : date.getSeconds(),
  ).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}${utc ? "Z" : ""}`;
};

/**
 * Format a relative time string (e.g. "2 hours ago").
 * Used in audit logs and user activity tables.
 *
 * @param {Date|string|number} value
 * @returns {string}
 */
export const formatRelativeTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";

  const diffMs = Date.now() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return formatDate(date);
};

// ---------------------------------------------------------------------------
// Phone
// ---------------------------------------------------------------------------

/**
 * Format a raw phone number input into E.164 Saudi format.
 * Handles common input patterns:
 *  "0512345678"   → "+966512345678"
 *  "512345678"    → "+966512345678"
 *  "966512345678" → "+966512345678"
 *  "+966512345678"→ "+966512345678" (already correct)
 *
 * Returns the original value if it cannot be normalised — the validator
 * will catch the format error.
 *
 * @param {string} value
 * @returns {string}
 */
export const formatSaudiPhone = (value) => {
  if (!value) return "";

  // Strip everything except digits and leading +
  const stripped = value.replace(/[^\d+]/g, "");

  // Already E.164
  if (/^\+966\d{9}$/.test(stripped)) return stripped;

  // Digits only — derive E.164
  const digits = stripped.replace(/^\+/, "");

  if (digits.startsWith("966") && digits.length === 12) {
    return `+${digits}`;
  }
  if (digits.startsWith("0") && digits.length === 10) {
    return `+966${digits.slice(1)}`;
  }
  if (digits.length === 9) {
    return `+966${digits}`;
  }

  // Cannot normalise — return as-is for validator to catch
  return value;
};

// ---------------------------------------------------------------------------
// Invoice / ZATCA identifiers
// ---------------------------------------------------------------------------

/**
 * Format an invoice file name per ZATCA file naming convention (section 14).
 * Pattern: {SellerVATNumber}_{Date}T{Time}_{InvoiceNumber}.xml
 * Example: "3101223935000030_20240115T143022_INV-2024-001.xml"
 *
 * @param {{
 *   vatNumber:     string,
 *   issueDate:     Date|string,
 *   issueTime:     Date|string,
 *   invoiceNumber: string,
 * }} params
 * @returns {string}
 */
export const formatInvoiceFileName = ({
  vatNumber,
  issueDate,
  issueTime,
  invoiceNumber,
}) => {
  const date = formatDateForXml(issueDate).replace(/-/g, "");
  const time = formatTimeForXml(issueTime).replace(/:/g, "");
  const safeInvoiceNumber = invoiceNumber.replace(/[^a-zA-Z0-9]/g, "-");
  return `${vatNumber}_${date}T${time}_${safeInvoiceNumber}.xml`;
};

/**
 * Truncate a long string with an ellipsis.
 * Used in tables where column width is limited.
 *
 * @param {string}  value
 * @param {number}  [maxLength=30]
 * @returns {string}
 */
export const truncate = (value, maxLength = 30) => {
  if (!value) return "—";
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}…`;
};

/**
 * Format a file size in bytes to a human-readable string.
 * Output: "4.2 MB", "512 KB"
 *
 * @param {number} bytes
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};
